import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as ClearCalender } from '../../assets/icons/clear-calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DateTime';
import MyButton from '../button/Button';

/**
 * Calendar grid properties.
 */
interface CalendarGridProperties {
  days: any[];
  onDayClick: (date: string, fullDate: Date) => void;
  getDayClass: (dateObject: Date | undefined) => string;
  onHover: (date: string | null) => void;
  weekdayLabels?: string[];
}

/**
 * CalendarGrid component to render day cells for a month.
 * @param {CalendarGridProperties} properties - The component properties.
 * @param {any[]} properties.days - The days array.
 * @param {Function} properties.onDayClick - Handler for day click.
 * @param {Function} properties.getDayClass - Function to get day CSS class.
 * @param {Function} properties.onHover - Handler for hovering over a day.
 * @param {string[]} [properties.weekdayLabels] - The localized weekday labels.
 * @returns {any} The rendered calendar grid.
 */
function CalendarGrid({
  days,
  onDayClick,
  getDayClass,
  onHover,
  weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
}: CalendarGridProperties): JSX.Element {
  return (
    <div className="calender-body">
      <div className="calender-header-items">
        {weekdayLabels.map((d, index) => (
          <div key={`${d}-${index}`} className="header-item-label">
            {d}
          </div>
        ))}
      </div>
      <div className="calender-body-items">
        {days.map((d: any, index: number) => {
          const isEmpty = Object.keys(d).length === 0;
          const isDisabled = Boolean(d.isDisabled);
          const dayClassName = [
            'day-item',
            isEmpty ? 'empty' : '',
            isDisabled ? 'disabled' : '',
            d.fullDate ? getDayClass(d.fullDate) : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={index}
              role="presentation"
              className={dayClassName}
              onClick={() => {
                if (!isEmpty && !isDisabled && d.date && d.fullDate) {
                  onDayClick(d.date, d.fullDate);
                }
              }}
              onMouseEnter={() => {
                if (!isEmpty && !isDisabled && d.date) {
                  onHover(d.date);
                }
              }}>
              {d.number}
            </div>
          );
        })}
      </div>
    </div>
  );
}

CalendarGrid.defaultProps = {
  weekdayLabels: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
};

/**
 * Supported preset keys for default date range selection.
 */
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'thisMonth'
  | 'oneMonth'
  | 'prevMonth'
  | 'thisWeek'
  | 'last7Days'
  | 'prevWeek'
  | 'prev2Week'
  | 'prev2Weeks'
  | 'last14Days'
  | 'this_month'
  | 'one_month'
  | 'prev_month'
  | 'this_week'
  | 'last_7_days'
  | 'prev_week'
  | 'prev_2_weeks'
  | 'last_14_days'
  | 'custom';

/**
 * CustomDateRangePicker properties interface.
 */
export interface CustomDateRangePickerProperties {
  inputLabel?: string;
  startDate?: string;
  endDate?: string;
  onChange: (start: string, end: string) => void;
  onClear?: () => void;
  clearable?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  valueRequired?: boolean;
  defaultDuration?: DateRangePreset;
  defaultPreset?: DateRangePreset;
  defaultDurationSelection?: DateRangePreset;
  isSingleDate?: boolean;
  singleDate?: boolean;
  disableDates?: 'past' | 'future';
  requiredTime?: boolean;
  timeFormat?: '24hr' | '12hr';
}

/**
 * Normalizes preset names (camelCase, snake_case, spaces) to standard internal key.
 * @param {DateRangePreset} [preset] - The preset value to normalize.
 * @returns {string} The normalized preset key.
 */
export const normalizePresetKey = (preset?: DateRangePreset): string => {
  if (!preset) return 'custom';
  switch (preset) {
    case 'today':
      return 'today';
    case 'yesterday':
      return 'yesterday';
    case 'thisMonth':
    case 'this_month':
      return 'this_month';
    case 'oneMonth':
    case 'one_month':
      return 'one_month';
    case 'prevMonth':
    case 'prev_month':
      return 'prev_month';
    case 'thisWeek':
    case 'this_week':
      return 'this_week';
    case 'last7Days':
    case 'last_7_days':
      return 'last_7_days';
    case 'prevWeek':
    case 'prev_week':
      return 'prev_week';
    case 'prev2Week':
    case 'prev2Weeks':
    case 'prev_2_weeks':
      return 'prev_2_weeks';
    case 'last14Days':
    case 'last_14_days':
      return 'last_14_days';
    case 'custom':
    default:
      return 'custom';
  }
};

/**
 * Calculates start and end dates for a given preset key.
 * @param {string} key - The preset key.
 * @param {'past' | 'future'} [disableDates] - Whether past or future dates are disabled.
 * @returns {{ startDate: string; endDate: string; startDateObj: Date; endDateObj: Date } | null} The calculated date range or null.
 */
export const calculateDateRange = (
  key: string,
  disableDates?: 'past' | 'future',
): { startDate: string; endDate: string; startDateObj: Date; endDateObj: Date } | null => {
  const today = new Date();
  let start: Date;
  let end: Date;

  switch (key) {
    case 'today':
      start = new Date(today);
      end = new Date(today);
      break;
    case 'yesterday':
      start = new Date(today);
      start.setDate(today.getDate() - 1);
      end = new Date(start);
      break;
    case 'this_month':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'one_month': // Last 30 days
      start = new Date(today);
      start.setDate(today.getDate() - 30);
      end = new Date(today);
      break;
    case 'prev_month':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'this_week': {
      start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case 'last_7_days':
      start = new Date(today);
      start.setDate(today.getDate() - 6);
      end = new Date(today);
      break;
    case 'prev_week': {
      const currentSunday = new Date(today);
      currentSunday.setDate(today.getDate() - today.getDay());
      start = new Date(currentSunday);
      start.setDate(currentSunday.getDate() - 7);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case 'prev_2_weeks': {
      const currentSunday = new Date(today);
      currentSunday.setDate(today.getDate() - today.getDay());
      end = new Date(currentSunday);
      end.setDate(currentSunday.getDate() - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 13);
      break;
    }
    case 'last_14_days':
      start = new Date(today);
      start.setDate(today.getDate() - 13);
      end = new Date(today);
      break;
    default:
      return null;
  }

  if (disableDates === 'future' && end > today) {
    end = new Date(today);
  }
  if (disableDates === 'past' && start < today) {
    start = new Date(today);
  }

  return {
    startDate: changeDateFormat(start.toString()),
    endDate: changeDateFormat(end.toString()),
    startDateObj: start,
    endDateObj: end,
  };
};

/**
 * Returns translated preset definitions.
 * @param {any} t - The translation function.
 * @returns {Array} List of preset definitions.
 */
export const getPresets = (t: any) => [
  { label: t('today', { defaultValue: 'Today' }), key: 'today', translationKey: 'today' },
  {
    label: t('yesterday', { defaultValue: 'Yesterday' }),
    key: 'yesterday',
    translationKey: 'yesterday',
  },
  {
    label: t('thisMonth', { defaultValue: 'This month' }),
    key: 'this_month',
    translationKey: 'thisMonth',
  },
  {
    label: t('oneMonth', { defaultValue: 'One month' }),
    key: 'one_month',
    translationKey: 'oneMonth',
  },
  {
    label: t('prevMonth', { defaultValue: 'Prev month' }),
    key: 'prev_month',
    translationKey: 'prevMonth',
  },
  {
    label: t('thisWeek', { defaultValue: 'This week' }),
    key: 'this_week',
    translationKey: 'thisWeek',
  },
  {
    label: t('last7Days', { defaultValue: 'Last 7 days' }),
    key: 'last_7_days',
    translationKey: 'last7Days',
  },
  {
    label: t('prevWeek', { defaultValue: 'Prev week' }),
    key: 'prev_week',
    translationKey: 'prevWeek',
  },
  {
    label: t('prev2Weeks', { defaultValue: 'Prev 2 weeks' }),
    key: 'prev_2_weeks',
    translationKey: 'prev2Weeks',
  },
  {
    label: t('last14Days', { defaultValue: 'Last 14 days' }),
    key: 'last_14_days',
    translationKey: 'last14Days',
  },
  {
    label: t('customRange', { defaultValue: 'Custom Range' }),
    key: 'custom',
    translationKey: 'customRange',
  },
];

/**
 * Returns translated list of month names.
 * @param {any} t - The translation function.
 * @returns {string[]} Array of 12 month names.
 */
export const getMonthsList = (t: any) => [
  t('january', { defaultValue: 'January' }),
  t('february', { defaultValue: 'February' }),
  t('march', { defaultValue: 'March' }),
  t('april', { defaultValue: 'April' }),
  t('may', { defaultValue: 'May' }),
  t('june', { defaultValue: 'June' }),
  t('july', { defaultValue: 'July' }),
  t('august', { defaultValue: 'August' }),
  t('september', { defaultValue: 'September' }),
  t('october', { defaultValue: 'October' }),
  t('november', { defaultValue: 'November' }),
  t('december', { defaultValue: 'December' }),
];

/**
 * Returns localized weekday labels starting from Sunday.
 * @param {string} [language] - Current language code.
 * @returns {string[]} Array of 7 weekday labels.
 */
export const getLocalizedWeekdayLabels = (language?: string): string[] => {
  if (language === 'ar') {
    return ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'];
  }
  if (language === 'fr') {
    return ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
  }
  if (language === 'sp' || language === 'es') {
    return ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
  }
  return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
};

/**
 * Formats month and year header string according to the active locale.
 * @param {number} month - The month index (0-11).
 * @param {number} year - The full year.
 * @param {string} [language] - The active language code.
 * @param {any} [t] - Optional translation function.
 * @returns {string} Formatted month and year header.
 */
export const formatMonthYearHeader = (
  month: number,
  year: number,
  language?: string,
  t?: any,
): string => {
  const date = new Date(year, month, 1);
  const locale = language === 'sp' ? 'es' : language || 'en';
  try {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  } catch {
    if (t) {
      const translatedMonths = getMonthsList(t);
      return `${translatedMonths[month]} ${year}`;
    }
    const defaultMonths = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${defaultMonths[month]} ${year}`;
  }
};

/**
 * This function is used to render the custom date range picker.
 * @param {CustomDateRangePickerProperties} properties - The component properties.
 * @param {string} [properties.inputLabel] - The label for the input field.
 * @param {string} [properties.startDate] - The startDate prop.
 * @param {string} [properties.endDate] - The endDate prop.
 * @param {Function} properties.onChange - The onChange prop.
 * @param {Function} [properties.onClear] - Optional callback invoked when the clear icon is clicked.
 * @param {boolean} [properties.clearable] - When false, the clear date button is never shown. Defaults to true.
 * @param {string} [properties.placeholder] - The placeholder prop.
 * @param {boolean} [properties.readOnly] - The readOnly prop.
 * @param {boolean} [properties.valueRequired] - The valueRequired prop.
 * @param {DateRangePreset} [properties.defaultDuration] - The default duration preset.
 * @param {DateRangePreset} [properties.defaultPreset] - The default preset alias.
 * @param {DateRangePreset} [properties.defaultDurationSelection] - The default duration selection alias.
 * @param {boolean} [properties.isSingleDate] - Whether the picker operates in single date selection mode.
 * @param {boolean} [properties.singleDate] - Alias for isSingleDate.
 * @param {'past' | 'future'} [properties.disableDates] - The disableDates prop.
 * @returns {any} The custom date range picker.
 */
function CustomDateRangePicker(properties: Readonly<CustomDateRangePickerProperties>): JSX.Element {
  const {
    inputLabel,
    startDate = '',
    endDate = '',
    onChange,
    onClear,
    clearable = true,
    placeholder,
    readOnly,
    valueRequired,
    defaultDuration,
    defaultPreset,
    defaultDurationSelection,
    isSingleDate,
    singleDate,
    disableDates,
    requiredTime,
    timeFormat = '24hr',
  } = properties;

  const isSingleMode = Boolean(isSingleDate || singleDate);

  const triggerReference = useRef<HTMLDivElement>(null);
  const reference = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation('customDate');
  const weekdayLabels = getLocalizedWeekdayLabels(i18n.language);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<any>({});

  const [startHours, setStartHours] = useState<number>(0);
  const [startMinutes, setStartMinutes] = useState<number>(0);
  const [startSeconds, setStartSeconds] = useState<number>(0);
  const [endHours, setEndHours] = useState<number>(23);
  const [endMinutes, setEndMinutes] = useState<number>(59);
  const [endSeconds, setEndSeconds] = useState<number>(59);
  
  const [activeTimeTab, setActiveTimeTab] = useState<'start' | 'end'>('start');

  const chosenDefault = defaultDuration || defaultPreset || defaultDurationSelection;
  const normalizedDefault = normalizePresetKey(chosenDefault);

  // View state for the LEFT calendar
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const [activePreset, setActivePreset] = useState<string>(() => {
    if (normalizedDefault !== 'custom') {
      return normalizedDefault;
    }
    return 'custom';
  });

  const [temporaryStartDate, setTemporaryStartDate] = useState(startDate);
  const [temporaryEndDate, setTemporaryEndDate] = useState(endDate);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  // Calendar Data
  const [leftCalendarDays, setLeftCalendarDays] = useState<any[]>([]);
  const [rightCalendarDays, setRightCalendarDays] = useState<any[]>([]);

  // Initialize view based on current startDate or today
  useEffect(() => {
    if (startDate) {
      const date = new Date(startDate);
      if (!Number.isNaN(date.getTime())) {
        setViewMonth(date.getMonth());
        setViewYear(date.getFullYear());
      }
    }
  }, [startDate, open]);

  // Update temp state when props change
  useEffect(() => {
    if (startDate) {
      const parts = startDate.split(' ');
      setTemporaryStartDate(parts[0]);
      if (parts[1]) {
        const [h, m, s] = parts[1].split(':');
        setStartHours(parseInt(h, 10) || 0);
        setStartMinutes(parseInt(m, 10) || 0);
        setStartSeconds(parseInt(s, 10) || 0);
      }
    } else {
      setTemporaryStartDate('');
      setStartHours(0);
      setStartMinutes(0);
      setStartSeconds(0);
    }

    if (endDate) {
      const parts = endDate.split(' ');
      setTemporaryEndDate(parts[0]);
      if (parts[1]) {
        const [h, m, s] = parts[1].split(':');
        setEndHours(parseInt(h, 10) || 0);
        setEndMinutes(parseInt(m, 10) || 0);
        setEndSeconds(parseInt(s, 10) || 0);
      }
    } else {
      setTemporaryEndDate('');
      setEndHours(23);
      setEndMinutes(59);
      setEndSeconds(59);
    }
  }, [startDate, endDate]);

  /**
   * HandleApplydfate time function
   */
  const handleApplyDateTime = () => {
    const sDate = temporaryStartDate || changeDateFormat(new Date().toString());
    const errorDate = temporaryEndDate || sDate;
    const sTime = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}:${String(startSeconds).padStart(2, '0')}`;
    const errorTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:${String(endSeconds).padStart(2, '0')}`;
    onChange(`${sDate} ${sTime}`, `${errorDate} ${errorTime}`);
    setOpen(false);
  };

  /**
   * Checks whether a given date should be disabled based on disableDates prop.
   * @param {Date} date - The date to check.
   * @returns {boolean} Whether the date is disabled.
   */
  const isDateDisabled = (date: Date): boolean => {
    if (!disableDates) return false;
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (disableDates === 'past') {
      return target.getTime() < today.getTime();
    }
    if (disableDates === 'future') {
      return target.getTime() > today.getTime();
    }
    return false;
  };

  /**
   * Checks whether a preset should be disabled based on disableDates prop.
   * @param {string} key - The preset key.
   * @returns {boolean} Whether the preset is disabled.
   */
  const isPresetDisabled = (key: string): boolean => {
    if (!disableDates || key === 'custom') return false;
    const range = calculateDateRange(key, disableDates);
    if (!range) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    const startTime = new Date(range.startDateObj).setHours(0, 0, 0, 0);
    const endTime = new Date(range.endDateObj).setHours(0, 0, 0, 0);

    if (disableDates === 'past') {
      return startTime < todayTime || endTime < todayTime;
    }
    if (disableDates === 'future') {
      return startTime > todayTime || endTime > todayTime;
    }
    return false;
  };

  // Apply default duration on mount if startDate and endDate are not provided
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (
        !startDate &&
        !endDate &&
        normalizedDefault !== 'custom' &&
        !isPresetDisabled(normalizedDefault)
      ) {
        const range = calculateDateRange(normalizedDefault, disableDates);
        if (range) {
          onChange(range.startDate, range.endDate);
          setTemporaryStartDate(range.startDate);
          setTemporaryEndDate(range.endDate);
          setActivePreset(normalizedDefault);
          setViewMonth(range.startDateObj.getMonth());
          setViewYear(range.startDateObj.getFullYear());
        }
      }
    }
  }, [startDate, endDate, normalizedDefault, onChange, disableDates]);

  // Handle dynamic changes to default duration prop
  const previousDefaultPreset = useRef(normalizedDefault);
  useEffect(() => {
    if (previousDefaultPreset.current !== normalizedDefault) {
      previousDefaultPreset.current = normalizedDefault;
      if (normalizedDefault !== 'custom' && !isPresetDisabled(normalizedDefault)) {
        const range = calculateDateRange(normalizedDefault, disableDates);
        if (range) {
          onChange(range.startDate, range.endDate);
          setTemporaryStartDate(range.startDate);
          setTemporaryEndDate(range.endDate);
          setActivePreset(normalizedDefault);
          setViewMonth(range.startDateObj.getMonth());
          setViewYear(range.startDateObj.getFullYear());
        }
      }
    }
  }, [normalizedDefault, onChange, disableDates]);

  /**
   * Generates an array of day objects for a given month and year.
   * @param {number} month - The month (0-11).
   * @param {number} year - The year.
   * @returns {any[]} An array of day objects.
   */
  const generateDays = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    const formattedDays = days.map((d) => ({
      date: changeDateFormat(d.toString()), // e.g. "YYYY-MM-DD" depending on util
      fullDate: d,
      number: d.getDate(),
      day: d.getDay(),
      isDisabled: isDateDisabled(d),
    }));

    // Fill padding for empty start slots
    if (formattedDays.length > 0 && formattedDays[0].day > 0) {
      const paddingCount = formattedDays[0].day;
      const padding = Array(paddingCount).fill({});
      return [...padding, ...formattedDays];
    }
    return formattedDays;
  };

  useEffect(() => {
    setLeftCalendarDays(generateDays(viewMonth, viewYear));

    // Right calendar is next month
    let rightMonth = viewMonth + 1;
    let rightYear = viewYear;
    if (rightMonth > 11) {
      rightMonth = 0;
      rightYear += 1;
    }
    setRightCalendarDays(generateDays(rightMonth, rightYear));
  }, [viewMonth, viewYear, disableDates]);

  /**
   * Calculates and sets the popup position relative to viewport.
   * @returns {void}
   */
  const calculatePosition = () => {
    const targetElement = triggerReference.current;
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const POPUP_HEIGHT = 450;
    const POPUP_WIDTH = requiredTime ? 950 : 690;
    const SCREEN_HEIGHT = window.innerHeight;
    const SCREEN_WIDTH = window.innerWidth;
    const isRtl = i18n.dir() === 'rtl';

    let left = isRtl ? rect.right - POPUP_WIDTH : rect.left;

    if (left + POPUP_WIDTH > SCREEN_WIDTH - 10) {
      left = SCREEN_WIDTH - POPUP_WIDTH - 10;
    }
    if (left < 10) {
      left = 10;
    }

    const styleObject: React.CSSProperties = { left };

    const spaceBelow = SCREEN_HEIGHT - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < POPUP_HEIGHT && spaceAbove > spaceBelow) {
      styleObject.bottom = SCREEN_HEIGHT - rect.top + 8;
      styleObject.top = 'auto';
    } else {
      styleObject.top = rect.bottom + 8;
      styleObject.bottom = 'auto';
    }

    setStyle(styleObject);
  };

  /**
   * Handles click outside the date picker component.
   * @param {MouseEvent} event - The mouse event.
   * @returns {void}
   */
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      reference.current &&
      !reference.current.contains(target) &&
      triggerReference.current &&
      !triggerReference.current.contains(target)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      calculatePosition();

      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition, true);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition, true);
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, i18n.language, i18n.dir()]);

  /**
   * Toggles the open state of the date picker.
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event.
   * @returns {void}
   */
  const toggleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (readOnly) return;
    if (!open) {
      calculatePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  /**
   * Checks if previous month navigation should be disabled.
   * @returns {boolean} Whether previous month navigation is disabled.
   */
  const isPreviousMonthDisabled = (): boolean => {
    if (disableDates === 'past') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      return viewYear < currentYear || (viewYear === currentYear && viewMonth <= currentMonth);
    }
    return false;
  };

  /**
   * Checks if next month navigation should be disabled.
   * @returns {boolean} Whether next month navigation is disabled.
   */
  const isNextMonthDisabled = (): boolean => {
    if (disableDates === 'future') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      return viewYear > currentYear || (viewYear === currentYear && viewMonth >= currentMonth);
    }
    return false;
  };

  /**
   * Handles month change.
   * @param {'prev' | 'next'} direction - The direction of the month change.
   */
  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (isPreviousMonthDisabled()) return;
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    } else if (direction === 'next') {
      if (isNextMonthDisabled()) return;
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear(viewYear + 1);
      } else {
        setViewMonth(viewMonth + 1);
      }
    }
  };

  /**
   * Handles preset click.
   * @param {string} key - The key of the preset.
   */
  const handlePresetClick = (key: string) => {
    if (isPresetDisabled(key)) return;
    setActivePreset(key);

    if (key === 'custom') return;

    const range = calculateDateRange(key, disableDates);
    if (!range) return;

    setTemporaryStartDate(range.startDate);
    setTemporaryEndDate(range.endDate);

    if (requiredTime) {
      const sTime = `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}:${String(startSeconds).padStart(2, '0')}`;
      const eventTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:${String(endSeconds).padStart(2, '0')}`;
      onChange(`${range.startDate} ${sTime}`, `${range.endDate} ${eventTime}`);
    } else {
      onChange(range.startDate, range.endDate);
    }
    setOpen(false); // Auto close on preset

    // Update view to start date
    setViewMonth(range.startDateObj.getMonth());
    setViewYear(range.startDateObj.getFullYear());
  };

  /**
   * Handles date click.
   * @param {string} dateString - The date string.
   * @param {Date} dateObject - The date object.
   */
  const handleDateClick = (dateString: string, dateObject: Date) => {
    if (isDateDisabled(dateObject)) return;
    setActivePreset('custom');

    if (isSingleMode) {
      setTemporaryStartDate(dateString);
      setTemporaryEndDate(dateString);
      if (!requiredTime) {
        onChange(dateString, dateString);
        setOpen(false);
      }
      return;
    }

    // If we have both, start over
    if (temporaryStartDate && temporaryEndDate) {
      setTemporaryStartDate(dateString);
      setTemporaryEndDate('');
      setActiveTimeTab('start');
      return;
    }

    // If we have start only
    if (temporaryStartDate && !temporaryEndDate) {
      const start = new Date(temporaryStartDate);
      start.setHours(0, 0, 0, 0);
      const clicked = new Date(dateObject);
      clicked.setHours(0, 0, 0, 0);

      if (clicked.getTime() < start.getTime()) {
        setTemporaryStartDate(dateString); // New start
        setActiveTimeTab('start');
      } else {
        setTemporaryEndDate(dateString); // Set end
        setActiveTimeTab('end');
        if (!requiredTime) {
          onChange(temporaryStartDate, dateString);
          setOpen(false);
        }
      }
      return;
    }

    // If we have nothing
    setTemporaryStartDate(dateString);
    setActiveTimeTab('start');
  };

  /**
   * Gets the class name for a date cell.
   * @param {Date} [dateObject] - The date object.
   * @returns {string} The class name.
   */
  const getDayClass = (dateObject: Date | undefined): string => {
    if (!dateObject) return 'empty';
    dateObject.setHours(0, 0, 0, 0);
    const time = dateObject.getTime();

    let sTime = 0;
    let eventTime = 0;

    if (temporaryStartDate) {
      const d = new Date(temporaryStartDate);
      d.setHours(0, 0, 0, 0);
      sTime = d.getTime();
    }
    if (temporaryEndDate) {
      const d = new Date(temporaryEndDate);
      d.setHours(0, 0, 0, 0);
      eventTime = d.getTime();
    }

    if (sTime && eventTime) {
      if (time === sTime && time === eventTime) return 'selected start-end';
      if (time === sTime) return 'selected start';
      if (time === eventTime) return 'selected end';
      if (time > sTime && time < eventTime) return 'in-range';
    } else if (sTime) {
      if (time === sTime) return 'selected start-end';
      if (hoverDate && !isSingleMode) {
        const hDate = new Date(hoverDate);
        hDate.setHours(0, 0, 0, 0);
        const hTime = hDate.getTime();
        if (time > sTime && time <= hTime) return 'in-range-preview';
      }
    }

    return '';
  };

  /**
   * Handles backdrop click.
   * @param {React.MouseEvent} event - The mouse event.
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
  };

  /**
   * Returns the formatted date string to display in the input box.
   * @returns {string} The formatted display text.
   */
  const getDisplayValue = (): string => {
    if (startDate && endDate) {
      if (startDate === endDate) {
        return startDate;
      }
      return `${startDate} - ${endDate}`;
    }
    if (startDate) {
      return startDate;
    }
    if (endDate) {
      return endDate;
    }
    return (
      placeholder ||
      (isSingleMode
        ? t('datePlaceholder', { defaultValue: 'DD-MMM-YYYY' })
        : t('dateRangePlaceholder', { defaultValue: 'DD-MMM-YYYY - DD-MMM-YYYY' }))
    );
  };

  /**
   * Handles clearing the selected date range.
   * Resets internal temporary dates, notifies onChange with empty strings,
   * and calls onClear callback if provided.
   * @param {React.MouseEvent<HTMLButtonElement>} event - The mouse event from the clear button.
   * @returns {void}
   */
  const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setTemporaryStartDate('');
    setTemporaryEndDate('');
    setActivePreset('custom');
    onChange('', '');
    if (onClear) {
      onClear();
    }
  };

  /**
   * Helper to render a time picker
   * @param {number} hours - hours state
   * @param {Function} setHours - hours setter
   * @param {number} minutes - minutes state
   * @param {Function} setMinutes - minutes setter
   * @param {number} seconds - seconds state
   * @param {Function} setSeconds - seconds setter
   */
  const renderTimePicker = (
    hours: number,
    setHours: (v: number) => void,
    minutes: number,
    setMinutes: (v: number) => void,
    seconds: number,
    setSeconds: (v: number) => void,
  ) => {
    return (
        <div className="time-picker-columns" style={{ height: '220px', marginBottom: 0, flex: 1 }}>
          <div className="time-column hours-col">
            <span className="column-label">HH</span>
            <div className="column-options">
              {Array.from({ length: timeFormat === '12hr' ? 12 : 24 }).map((_, index) => {
                const hour = timeFormat === '12hr' ? index + 1 : index;
                const hourString = String(hour).padStart(2, '0');
                const isSelected =
                  timeFormat === '12hr' ? (hours % 12 || 12) === hour : hours === hour;
                return (
                  <button
                    key={hour}
                    type="button"
                    className={`time-option ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                      if (timeFormat === '12hr') {
                        setHours((hours >= 12 ? 12 : 0) + (hour % 12));
                      } else {
                        setHours(hour);
                      }
                    }}>
                    {hourString}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="time-column minutes-col">
            <span className="column-label">MM</span>
            <div className="column-options">
              {Array.from({ length: 60 }).map((_, minute) => (
                <button
                  key={minute}
                  type="button"
                  className={`time-option ${minutes === minute ? 'active' : ''}`}
                  onClick={() => setMinutes(minute)}>
                  {String(minute).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
          <div className="time-column seconds-col">
            <span className="column-label">SS</span>
            <div className="column-options">
              {Array.from({ length: 60 }).map((_, second) => (
                <button
                  key={second}
                  type="button"
                  className={`time-option ${seconds === second ? 'active' : ''}`}
                  onClick={() => setSeconds(second)}>
                  {String(second).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
          {timeFormat === '12hr' && (
            <div className="time-column meridiem-col">
              <span className="column-label">AM/PM</span>
              <div className="column-options">
                {(['AM', 'PM'] as const).map((meridiem) => {
                  const isActive =
                    (hours < 12 && meridiem === 'AM') || (hours >= 12 && meridiem === 'PM');
                  return (
                    <button
                      key={meridiem}
                      type="button"
                      className={`time-option ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        const hour12 = hours % 12;
                        setHours(meridiem === 'PM' ? hour12 + 12 : hour12);
                      }}>
                      {meridiem}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
    );
  };

  /** Determines whether a date range is currently selected. */
  const hasDateRange = Boolean(startDate || endDate);

  return (
    <div className="cdrp" dir={i18n.dir()}>
      {inputLabel && (
        <p className="cdrp__label">
          {inputLabel}
          {valueRequired && <span className="cdrp__label-required">*</span>}
        </p>
      )}

      <div className="cdrp__input-row">
        {clearable && hasDateRange && !readOnly && (
          <button
            type="button"
            className={`cdrp__clear-btn${i18n.dir() === 'rtl' ? ' cdrp__clear-btn--rtl' : ''}`}
            onClick={handleClear}
            aria-label={t('clearDateRange', { defaultValue: 'Clear date range' })}>
            <ClearCalender />
          </button>
        )}
        <div
          ref={triggerReference}
          className="cdrp__trigger"
          onClick={toggleOpen}
          role="presentation">
          <div className="cdrp__value">{getDisplayValue()}</div>
          <span className="cdrp__calendar-icon">
            <Calender />
          </span>
        </div>

        {open &&
          ReactDOM.createPortal(
            <div className="cdrp-portal">
              <div
                className="cdrp-portal__backdrop"
                role="presentation"
                onClick={handleBackdropClick}
              />
              <div
                className={`cdrp-popup${i18n.dir() === 'rtl' ? ' cdrp-popup--rtl' : ''}`}
                ref={reference}
                style={style}
                dir={i18n.dir()}>
                {/* Sidebar */}
                <div className="cdrp-popup__sidebar">
                  {getPresets(t).map((p) => {
                    const isPresetItemDisabled = isPresetDisabled(p.key);
                    const presetClassName = [
                      'cdrp-popup__preset-item',
                      activePreset === p.key ? 'cdrp-popup__preset-item--active' : '',
                      isPresetItemDisabled ? 'cdrp-popup__preset-item--disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <div
                        key={p.key}
                        role="presentation"
                        className={presetClassName}
                        onClick={() => handlePresetClick(p.key)}>
                        {t(p.translationKey) || p.label}
                      </div>
                    );
                  })}
                </div>

                {/* Calendars Container */}
                <div className="cdrp-popup__calendars">
                    {/* Left Calendar */}
                    <div className="cdrp-popup__calendar">
                      <div className="cdrp-popup__cal-header">
                        <div
                          className={`cdrp-popup__cal-nav cdrp-popup__cal-nav--prev${isPreviousMonthDisabled() ? ' cdrp-popup__cal-nav--disabled' : ''}`}
                          role="presentation"
                          onClick={() => handleMonthChange('prev')}>
                          <LeftArrow className="cdrp-popup__nav-icon" />
                        </div>
                        <div className="cdrp-popup__cal-title">
                          {formatMonthYearHeader(viewMonth, viewYear, i18n.language, t)}
                        </div>
                        <div className="cdrp-popup__cal-nav cdrp-popup__cal-nav--hidden">
                          {/* Hidden arrow to balance layout */}
                          <RightArrow className="cdrp-popup__nav-icon" />
                        </div>
                      </div>
                      <CalendarGrid
                        days={leftCalendarDays}
                        onDayClick={handleDateClick}
                        getDayClass={getDayClass}
                        onHover={setHoverDate}
                        weekdayLabels={weekdayLabels}
                      />
                    </div>

                    {/* Right Calendar */}
                    <div className="cdrp-popup__calendar">
                      <div className="cdrp-popup__cal-header">
                        <div className="cdrp-popup__cal-nav cdrp-popup__cal-nav--hidden">
                          <LeftArrow className="cdrp-popup__nav-icon" />
                        </div>
                        <div className="cdrp-popup__cal-title">
                          {formatMonthYearHeader(
                            viewMonth + 1 > 11 ? 0 : viewMonth + 1,
                            viewMonth + 1 > 11 ? viewYear + 1 : viewYear,
                            i18n.language,
                            t,
                          )}
                        </div>
                        <div
                          className={`cdrp-popup__cal-nav cdrp-popup__cal-nav--next${isNextMonthDisabled() ? ' cdrp-popup__cal-nav--disabled' : ''}`}
                          role="presentation"
                          onClick={() => handleMonthChange('next')}>
                          <RightArrow className="cdrp-popup__nav-icon" />
                        </div>
                      </div>
                      <CalendarGrid
                        days={rightCalendarDays}
                        onDayClick={handleDateClick}
                        getDayClass={getDayClass}
                        onHover={setHoverDate}
                        weekdayLabels={weekdayLabels}
                      />
                    </div>
                </div>

                {requiredTime && (
                  <div className="calender-time-side" style={{ minWidth: '220px' }}>
                    <div className="time-side-title">
                      {t('selectTime', { defaultValue: 'Select Time' })}
                    </div>
                    
                    <div style={{ display: 'flex', background: 'var(--grey-3, #f2f4f7)', borderRadius: '8px', padding: '4px', marginBottom: '16px' }}>
                      <button 
                        type="button" 
                        style={{ flex: 1, padding: '6px 0', borderRadius: '6px', border: 'none', background: activeTimeTab === 'start' ? '#fff' : 'transparent', color: activeTimeTab === 'start' ? 'var(--primary-color, #0fbba6)' : 'var(--grey-text, #667085)', fontSize: '13px', fontWeight: activeTimeTab === 'start' ? 600 : 500, boxShadow: activeTimeTab === 'start' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer' }}
                        onClick={() => setActiveTimeTab('start')}
                      >
                        {t('startTime', { defaultValue: 'Start Time' })}
                      </button>
                      <button 
                        type="button" 
                        style={{ flex: 1, padding: '6px 0', borderRadius: '6px', border: 'none', background: activeTimeTab === 'end' ? '#fff' : 'transparent', color: activeTimeTab === 'end' ? 'var(--primary-color, #0fbba6)' : 'var(--grey-text, #667085)', fontSize: '13px', fontWeight: activeTimeTab === 'end' ? 600 : 500, boxShadow: activeTimeTab === 'end' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', cursor: 'pointer' }}
                        onClick={() => setActiveTimeTab('end')}
                      >
                        {t('endTime', { defaultValue: 'End Time' })}
                      </button>
                    </div>

                    {activeTimeTab === 'start'
                      ? renderTimePicker(startHours, setStartHours, startMinutes, setStartMinutes, startSeconds, setStartSeconds)
                      : renderTimePicker(endHours, setEndHours, endMinutes, setEndMinutes, endSeconds, setEndSeconds)}

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-grey)' }}>
                      <MyButton
                        label={t('cancel', { defaultValue: 'Cancel' })}
                        buttonType="outline"
                        onClickFunc={() => setOpen(false)}
                      />
                      <MyButton
                        label={t('apply', { defaultValue: 'Apply' })}
                        buttonType="primary"
                        onClickFunc={handleApplyDateTime}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

/**
 * Default props for CustomDateRangePicker component.
 */
CustomDateRangePicker.defaultProps = {
  inputLabel: '',
  startDate: '',
  endDate: '',
  valueRequired: false,
  placeholder: '',
  readOnly: false,
  clearable: true,
  onClear: undefined,
  defaultDuration: undefined,
  defaultPreset: undefined,
  defaultDurationSelection: undefined,
  isSingleDate: false,
  singleDate: false,
  disableDates: undefined,
  requiredTime: false,
  timeFormat: '24hr',
};

export default CustomDateRangePicker;
